import React from "react";
import { Popover, Tooltip } from "antd";
import { 
  NumberOutlined, 
  CheckOutlined, 
  FontSizeOutlined , 
  ClockCircleOutlined,
  EnvironmentOutlined,
  FileOutlined,
  GlobalOutlined,
  CodeOutlined
} from "@ant-design/icons";

interface LiteralExpressionProps {
  value: string;
  dataType?: string;
  placeHolder: string;
  onChange: (value: string) => void;
  onDataTypeChange: (value: string) => void;
  showDataTypeSelect?: boolean;
}

const dataTypeIcons: { [key: string]: React.ReactNode } = {
  "Logical Truth": <CheckOutlined />,
  "Binary Bit": <CodeOutlined />,
  "Integer Number": <NumberOutlined />,
  "Floating Point Number": <NumberOutlined />,
  "Character String": <FontSizeOutlined  />,
  "Text Object": <FileOutlined />,
  "Instance Object": <ClockCircleOutlined />,
  "Interval Object": <ClockCircleOutlined />,
  "Geometry Object": <EnvironmentOutlined />,
  "Geography Object": <GlobalOutlined />,
  "Binary Structured": <CodeOutlined />,
  "Binary Unstructured": <CodeOutlined />
};

export const LiteralExpression: React.FC<LiteralExpressionProps> = ({ 
  value, 
  dataType = "Character String",
  placeHolder, 
  onChange, 
  onDataTypeChange,
  showDataTypeSelect = true
}) => {
  const [popoverOpen, setPopoverOpen] = React.useState(false);

  const getInputType = (dataType: string): string => {
    switch(dataType) {
      case "Integer Number":
        return "number";
      case "Floating Point Number":
        return "number";
      case "Binary Bit":
        return "checkbox";
      case "Logical Truth":
        return "checkbox";
      case "Instance Object":
        return "datetime-local";
      case "Interval Object":
        return "time";
      default:
        return "text";
    }
  };

  const inputType = getInputType(dataType);

  const dataTypeContent = (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
      {Object.entries(dataTypeIcons).map(([type, icon]) => (
        <div
          key={type}
          onClick={() => {
            onDataTypeChange(type);
            setPopoverOpen(false);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px',
            cursor: 'pointer',
            backgroundColor: dataType === type ? '#e6f7ff' : 'transparent',
            borderRadius: '4px'
          }}
        >
          {icon} {type}
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
      <input
        type={inputType}
        value={value}
        checked={inputType === "checkbox" ? value === "true" : undefined}
        onChange={(e) => {
          const newValue = inputType === "checkbox" ? e.target.checked.toString() : e.target.value;
          onChange(newValue);
        }}
        placeholder={placeHolder}
        step={dataType === "Floating Point Number" ? "0.01" : "1"}
      />
      {showDataTypeSelect && (
        <Tooltip title={dataType}>
          <Popover 
            content={dataTypeContent} 
            trigger="click"
            placement="bottom"
            open={popoverOpen}
            onOpenChange={setPopoverOpen}
          >
            <div style={{ cursor: 'pointer' }}>
              {dataTypeIcons[dataType]}
            </div>
          </Popover>
        </Tooltip>
      )}
    </div>
  );
}; 